import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RcFile } from "antd/es/upload";
import { AxiosError } from "axios";
import { ErrorResponse } from "react-router-dom";
import {
  CsvDuplicateRow,
  CsvErrorRow,
  CsvReactivatedClient,
  showCsvDuplicateModal,
  showCsvErrorModal,
  showCsvReactivatedModal,
} from "components/modals/csvErrorModal";
import { translate } from "shared/languages";
import { getApiSuccessMessage, getApiErrorMessage } from "shared/utils";
import client from "shared/utils/axios-config";
import { invalidateEmployeeLists } from "hooks/mutations/employeeLists";

type UploadCsvVariables = {
  endpoint: string;
  file: RcFile;
  /** Set by Impor Tetap after the Klien import's duplicate warning. */
  confirmDuplicates?: boolean;
};

const useUploadCsvFile = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ endpoint, file, confirmDuplicates }: UploadCsvVariables) => {
      const formData = new FormData();
      formData.append("file", file);
      return client.post(
        `/v1/${endpoint}/csv${confirmDuplicates ? "?confirm_duplicates=true" : ""}`,
        formData,
      );
    },
    onSuccess: ({ data: response }) => {
      const endpoint = response.data;
      // Invalidate all queries based on their access type
      queryClient.invalidateQueries({ queryKey: [`${endpoint}`] });
      // The staff import creates instructors; the server answers "staff",
      // which is not the key the Instruktur / Karyawan lists cache under.
      if (endpoint === "staff") invalidateEmployeeLists(queryClient);
      queryClient.invalidateQueries({ queryKey: [`notification`] });
      // The class import also creates catalog products/tiers.
      queryClient.invalidateQueries({ queryKey: ["product"] });
      queryClient.invalidateQueries({ queryKey: ["package"] });
      getApiSuccessMessage(`CSV ${translate("messages.success.upload")}`);
      const reactivated = (
        response.meta as { reactivated?: CsvReactivatedClient[] } | undefined
      )?.reactivated;
      if (reactivated?.length) showCsvReactivatedModal(reactivated);
    },
    onError: (error: AxiosError<ErrorResponse>, variables: UploadCsvVariables) => {
      // The Klien import's 409: rows that look like a package the client
      // already holds (or an earlier row of the file). Nothing was written;
      // the admin decides, and Impor Tetap sends the same file again with the
      // override.
      const duplicates = (
        error.response?.data as { data?: { duplicates?: unknown } } | undefined
      )?.data?.duplicates;
      if (
        error.response?.status === 409 &&
        Array.isArray(duplicates) &&
        duplicates.length > 0
      ) {
        showCsvDuplicateModal(duplicates as CsvDuplicateRow[], () =>
          mutation.mutate({ ...variables, confirmDuplicates: true }),
        );
        return;
      }
      // The importers' 422 carries the per-row error table
      // (UploadCsvValidationError details) — show it as a real report; the
      // generic toast only knows string errors.
      const details = (error.response?.data as { error?: unknown } | undefined)
        ?.error;
      if (
        error.response?.status === 422 &&
        Array.isArray(details) &&
        details.length > 0
      ) {
        showCsvErrorModal(details as CsvErrorRow[]);
        return;
      }
      getApiErrorMessage(error);
    },
  });
  return mutation;
};

export { useUploadCsvFile };
